import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import LandingHeroDemo from '../components/LandingHeroDemo';
import StickyCta from '../components/StickyCta';
import LandingQuiz from '../components/LandingQuiz';
import { setMetaTags } from '../utils/seo';
import { useABVariant } from '../utils/abVariant';
import { heroVariants } from '../data/heroVariants';
import {
  Sparkles,
  ArrowRight,
  CheckCircle2,
  Brain,
  Target,
  Users,
  Zap,
  Shield,
  TrendingUp,
  Heart,
  IndianRupee,
  Rocket,
  ShieldCheck,
  Quote,
  Star,
} from 'lucide-react';

const features = [
  {
    icon: Brain,
    title: 'AI Resume Parsing',
    desc: 'Drop a resume — extract skills, experience, and contact in seconds.',
  },
  {
    icon: Target,
    title: 'Smart Matching',
    desc: 'Rank candidates against any job with rule-based + AI scoring.',
  },
  {
    icon: Users,
    title: 'Recruiter Command Center',
    desc: 'Single-screen kanban that replaces 5 tabs of legacy ATS.',
  },
  {
    icon: Zap,
    title: 'Bulk Operations',
    desc: 'Excel imports, bulk re-uploads, AI shortlists — built for high-volume hiring.',
  },
  {
    icon: Shield,
    title: 'Multi-tenant SaaS',
    desc: 'Each staffing firm gets its own isolated workspace. Data never crosses orgs.',
  },
  {
    icon: TrendingUp,
    title: 'Live Metrics',
    desc: 'Submissions, interviews, closures, TAT — auto-refresh on every screen.',
  },
];

const Landing = () => {
  const heroV = useABVariant('hero', heroVariants);

  useEffect(() => {
    setMetaTags({
      title: 'InterviewWala ATS — Hire smarter. Hire faster.',
      description:
        'The only ATS built for high-volume Indian staffing firms — bulk resume uploads, AI matching, single-screen Recruiter Command Center, and live pipeline metrics.',
      url: '/',
      image: '/demo/01-dashboard.jpg',
      type: 'website',
    });
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="landing-page">
      <StickyCta threshold={600} />

      {/* Nav */}
      <nav className="fixed top-0 inset-x-0 z-30 backdrop-blur-md bg-slate-950/60 border-b border-slate-800">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 xl:px-14 h-16 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2"
            data-testid="landing-logo-home"
          >
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-slate-900" />
            </div>
            <span
              className="text-lg font-semibold"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              InterviewWala
            </span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-4">
            <Link
              to="/pricing"
              className="text-sm text-slate-300 hover:text-white transition px-3 py-2"
              data-testid="landing-nav-pricing"
            >
              Pricing
            </Link>
            <Link
              to="/blog"
              className="text-sm text-slate-300 hover:text-white transition px-3 py-2"
              data-testid="landing-nav-blog"
            >
              Blog
            </Link>
            <Link
              to="/login"
              className="text-sm text-slate-300 hover:text-white transition px-3 py-2"
              data-testid="landing-nav-login"
            >
              Sign in
            </Link>
            <Link to="/signup" data-testid="landing-nav-signup">
              <Button
                size="sm"
                className="bg-gradient-to-r from-teal-400 to-emerald-500 text-slate-900 hover:opacity-90 font-semibold"
              >
                Start free
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-20 px-6 lg:px-10 xl:px-14">
        <div className="max-w-[1440px] mx-auto grid lg:grid-cols-[5fr_6fr] gap-12 lg:gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-teal-500/30 bg-teal-500/10 text-teal-300 text-xs font-medium mb-6">
              <Sparkles className="w-3 h-3" /> AI-powered ATS for staffing firms
            </div>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] mb-6"
              style={{ fontFamily: 'Manrope, sans-serif' }}
              data-testid="landing-hero-title"
              data-ab-variant={heroV.id}
            >
              {heroV.line1}<br />
              <span className="bg-gradient-to-r from-teal-300 to-emerald-400 bg-clip-text text-transparent">
                {heroV.line2}
              </span>
            </h1>
            <p
              className="text-base sm:text-lg text-slate-300 mb-8 max-w-xl leading-relaxed"
              data-testid="landing-hero-subhead"
            >
              {heroV.subhead}
            </p>
            <div className="flex flex-wrap gap-3 mb-10">
              <Link to="/signup" data-testid="landing-hero-signup">
                <Button
                  size="lg"
                  className="bg-gradient-to-r from-teal-400 to-emerald-500 text-slate-900 hover:opacity-90 font-semibold h-12 px-6"
                >
                  Start your free workspace <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
              <Link to="/pricing" data-testid="landing-hero-pricing">
                <Button
                  size="lg"
                  variant="outline"
                  className="h-12 px-6 border-slate-600 text-slate-100 hover:bg-slate-800 bg-transparent"
                >
                  See pricing
                </Button>
              </Link>
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-slate-400">
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4 text-teal-400" /> No credit card required
              </span>
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4 text-teal-400" /> Free forever for small teams
              </span>
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4 text-teal-400" /> Setup in under 2 minutes
              </span>
            </div>
          </div>

          {/* Visual block — auto-cycling product demo */}
          <LandingHeroDemo />
        </div>
      </section>

      {/* Social-proof / value bar */}
      <section
        className="px-6 lg:px-10 py-10 border-y border-teal-500/20 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950"
        data-testid="landing-social-proof"
      >
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-center gap-2 mb-5">
            <Heart className="w-4 h-4 text-rose-400 fill-rose-400/30" />
            <p className="text-sm font-semibold tracking-wide text-slate-200">
              Built by recruiters, for recruiters.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-2">
            {[
              {
                icon: Rocket,
                label: 'Built for high-volume staffing',
                color: 'text-teal-400',
              },
              {
                icon: IndianRupee,
                label: 'GST-inclusive INR pricing',
                color: 'text-emerald-400',
              },
              {
                icon: CheckCircle2,
                label: 'Free forever for small teams',
                color: 'text-amber-400',
              },
              {
                icon: ShieldCheck,
                label: 'Workspace data isolation',
                color: 'text-sky-400',
              },
            ].map((p) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.label}
                  className="flex items-center gap-2 justify-center sm:justify-start text-slate-400"
                  data-testid={`landing-social-pillar-${p.label
                    .toLowerCase()
                    .replace(/\s+/g, '-')
                    .replace(/[^a-z0-9-]/g, '')}`}
                >
                  <Icon className={`w-4 h-4 ${p.color} flex-shrink-0`} />
                  <span className="text-xs font-medium leading-tight">
                    {p.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* "Is it for me?" quiz */}
      <LandingQuiz />

      {/* Features */}
      <section className="py-20 px-6 lg:px-10 xl:px-14 border-t border-slate-800/50">
        <div className="max-w-[1440px] mx-auto">
          <h2
            className="text-3xl sm:text-4xl font-bold text-center mb-3"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Built for staffing teams who hire <span className="text-teal-400">at scale</span>
          </h2>
          <p className="text-slate-400 text-center max-w-2xl mx-auto mb-14">
            Every feature designed around the realities of high-volume recruitment — multiple
            clients, thousands of candidates, fast turnarounds.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((f) => {
              const Icon = f.icon;
              return (
                <div
                  key={f.title}
                  className="rounded-xl border border-slate-800 bg-slate-900/40 p-6 hover:border-teal-500/40 hover:bg-slate-900/80 transition"
                  data-testid={`landing-feature-${f.title.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="w-10 h-10 rounded-lg bg-teal-500/10 border border-teal-500/30 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-teal-400" />
                  </div>
                  <h3 className="font-semibold text-base mb-1.5">{f.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonial — REPLACE quote/name/role with a real recruiter quote when you have one */}
      <section
        className="px-6 lg:px-10 py-16"
        data-testid="landing-testimonial"
      >
        <div className="max-w-3xl mx-auto">
          <div className="relative rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-900 via-slate-900/60 to-slate-950 p-8 sm:p-10 overflow-hidden">
            {/* Decorative quote glyph */}
            <Quote
              className="absolute top-5 left-5 w-10 h-10 text-teal-500/20 fill-teal-500/10 -scale-x-100"
              strokeWidth={1.5}
            />
            <Quote
              className="absolute bottom-5 right-5 w-10 h-10 text-teal-500/20 fill-teal-500/10"
              strokeWidth={1.5}
            />

            {/* 5-star row */}
            <div
              className="flex items-center gap-0.5 mb-5"
              aria-hidden
              data-testid="landing-testimonial-stars"
            >
              {[0, 1, 2, 3, 4].map((i) => (
                <Star
                  key={i}
                  className="w-4 h-4 text-amber-400 fill-amber-400"
                />
              ))}
            </div>

            {/* Quote */}
            <blockquote
              className="text-xl sm:text-2xl text-slate-100 leading-relaxed font-medium"
              style={{ fontFamily: 'Manrope, sans-serif' }}
              data-testid="landing-testimonial-quote"
            >
              <span className="text-teal-300">“</span>
              I shortlisted 5 candidates in 30 seconds — used to take me half a day.
              <span className="text-teal-300">”</span>
            </blockquote>

            {/* Author */}
            <div className="flex items-center gap-3 mt-6">
              <div
                className="w-11 h-11 rounded-full bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center text-slate-900 font-bold flex-shrink-0"
                aria-hidden
              >
                R
              </div>
              <div>
                <p
                  className="text-sm font-semibold text-slate-100"
                  data-testid="landing-testimonial-author"
                >
                  Riya Mehra
                </p>
                <p className="text-xs text-slate-400">
                  Senior Recruiter · Acme Staffing
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 lg:px-10">
        <div className="max-w-4xl mx-auto text-center rounded-2xl border border-teal-500/30 bg-gradient-to-br from-teal-500/10 via-emerald-500/5 to-transparent p-10 sm:p-14">
          <h2
            className="text-3xl sm:text-4xl font-bold mb-3"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Your first recruiter is on us.
          </h2>
          <p className="text-slate-300 mb-7 max-w-xl mx-auto">
            Spin up your workspace in under 2 minutes. Free forever for small teams. No credit card.
          </p>
          <Link to="/signup" data-testid="landing-cta-signup">
            <Button
              size="lg"
              className="bg-gradient-to-r from-teal-400 to-emerald-500 text-slate-900 hover:opacity-90 font-semibold h-12 px-8"
            >
              Create free workspace <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="border-t border-slate-800 py-8 px-6 lg:px-10 xl:px-14">
        <div className="max-w-[1440px] mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
          <span>© 2026 InterviewWala ATS. Built for staffing firms.</span>
          <div className="flex gap-5">
            <Link to="/pricing" className="hover:text-slate-300">
              Pricing
            </Link>
            <Link to="/blog" className="hover:text-slate-300">
              Blog
            </Link>
            <Link to="/login" className="hover:text-slate-300">
              Sign in
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
